import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  hasHeadingLevelGap,
  hasTableOfContents,
  hasUniformFormatting,
  headings,
  paragraphsWithStyle,
  readDocument,
} from "@/office/word/document";

export const lektionInhaltsverzeichnis: Lesson = {
  id: "inhaltsverzeichnis",
  title: "Inhaltsverzeichnis und Abschlussübung",
  summary: "Die Belohnung für saubere Formatvorlagen: ein Verzeichnis auf Knopfdruck.",
  durationMinutes: 12,
  learningGoals: [
    "Ein automatisches Inhaltsverzeichnis einfügen und aktualisieren",
    "Formatvorlagen in einem vollständigen Dokument anwenden",
  ],
  steps: [
    {
      id: "verzeichnis-einfuegen",
      title: "Das Inhaltsverzeichnis einfügen",
      explanation: [
        "Ein Inhaltsverzeichnis schreibt man in Word nicht – man lässt es erzeugen. Word " +
          "sammelt dafür alle Absätze mit Überschriftenvorlagen ein und trägt sie samt " +
          "Seitenzahl ein. Alles, was du dafür tun musstest, hast du bereits erledigt.",
        "Setze den Cursor an die Stelle, an der das Verzeichnis stehen soll – " +
          "üblicherweise direkt unter den Titel. Wechsle dann auf die Registerkarte " +
          "„Verweise“ und wähle „Inhaltsverzeichnis“ und eine der automatischen Tabellen.",
        "Das Verzeichnis aktualisiert sich nicht von allein. Nach Änderungen klickst du " +
          "es an und wählst „Inhaltsverzeichnis aktualisieren“ – oder du drückst F9. " +
          "Beim Aktualisieren fragt Word, ob nur die Seitenzahlen oder das gesamte " +
          "Verzeichnis erneuert werden sollen.",
      ],
      keyPoints: [
        "Verweise → Inhaltsverzeichnis → automatische Tabelle.",
        "Aktualisieren mit F9, nicht von Hand nachtippen.",
        "Nur Absätze mit Überschriftenvorlagen landen im Verzeichnis.",
      ],
      task: {
        instruction: "Füge deinem Dokument ein automatisches Inhaltsverzeichnis hinzu.",
        hint: "Cursor unter den Titel setzen, dann Verweise → Inhaltsverzeichnis → „Automatische Tabelle 1“.",
        check: async () => {
          const snapshot = await readDocument();
          if (headings(snapshot).length === 0) {
            return fail(
              "Ohne Überschriften bleibt das Verzeichnis leer.",
              "Weise zuerst wieder Überschriftenvorlagen zu und füge das Verzeichnis dann ein.",
            );
          }
          if (!hasTableOfContents(snapshot)) {
            return fail(
              "Es wurde noch kein Inhaltsverzeichnis gefunden.",
              "Achte darauf, eine der automatischen Tabellen zu wählen – eine manuell getippte Liste zählt nicht.",
            );
          }
          return pass("Das Inhaltsverzeichnis steht und listet deine Kapitel auf.");
        },
      },
    },
    {
      id: "abschluss",
      title: "Abschlussübung: Ein strukturierter Bericht",
      explanation: [
        "Zum Abschluss baust du ein vollständiges Dokument – am besten in einem neuen, " +
          "leeren Dokument (Strg + N). Denk dir einen kurzen Bericht aus, zum Beispiel " +
          "einen Projektstand oder ein Protokoll.",
        "Diesmal formatierst du nichts mehr von Hand: Jeder Absatz bekommt die " +
          "Formatvorlage, die zu seiner Rolle passt.",
      ],
      keyPoints: [
        "Titel oben, danach das Inhaltsverzeichnis, dann die Kapitel.",
        "Genau eine „Überschrift 1“, darunter mindestens drei „Überschrift 2“.",
        "Fließtext in „Standard“ – ohne manuelle Formatierung.",
      ],
      task: {
        instruction:
          "Erstelle einen Bericht mit Titel, Inhaltsverzeichnis, genau einer " +
          "„Überschrift 1“, mindestens drei „Überschrift 2“ und mindestens fünf " +
          "Fließtextabsätzen in „Standard“. Die Ebenen dürfen keine Sprünge enthalten " +
          "und alle Überschriften derselben Ebene müssen einheitlich aussehen.",
        hint: "Schreibe erst den gesamten Text und weise danach von oben nach unten die Vorlagen zu. Das Inhaltsverzeichnis fügst du zum Schluss ein.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          const ebene1 = alle.filter((eintrag) => eintrag.level === 1).map((e) => e.paragraph);
          const ebene2 = alle.filter((eintrag) => eintrag.level === 2).map((e) => e.paragraph);
          const titel = paragraphsWithStyle(snapshot, "Title");
          const standard = paragraphsWithStyle(snapshot, "Normal");

          return fromCriteria(
            [
              criterion("Ein Absatz in der Vorlage „Titel“", titel.length > 0),
              criterion("Ein automatisches Inhaltsverzeichnis", hasTableOfContents(snapshot)),
              criterion(
                `Genau eine „Überschrift 1“ (gefunden: ${ebene1.length})`,
                ebene1.length === 1,
              ),
              criterion(
                `Mindestens drei „Überschrift 2“ (gefunden: ${ebene2.length})`,
                ebene2.length >= 3,
              ),
              criterion("Keine übersprungene Überschriftenebene", !hasHeadingLevelGap(snapshot)),
              criterion(
                `Mindestens fünf Absätze in „Standard“ (gefunden: ${standard.length})`,
                standard.length >= 5,
              ),
              criterion(
                "Alle „Überschrift 2“ sind einheitlich formatiert",
                ebene2.length >= 3 && hasUniformFormatting(ebene2),
              ),
            ],
            {
              success:
                "Sehr stark – dein Dokument ist vollständig über Formatvorlagen aufgebaut.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Uneinheitliche Überschriften entstehen fast immer durch direkte Formatierung. Markiere sie und drücke Strg + Leertaste, danach greift wieder die Vorlage.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Du weißt jetzt, warum Formatvorlagen mehr sind als eine Abkürzung beim " +
          "Formatieren: Sie geben jedem Absatz eine Rolle. Daraus entstehen Gliederung, " +
          "Navigationsbereich, Inhaltsverzeichnis und ein einheitliches Erscheinungsbild – " +
          "und zwar automatisch.",
        "Der Aufwand zahlt sich mit jeder Seite mehr aus. In einem zehnseitigen Bericht " +
          "ist eine geänderte Vorlage eine Sache von Sekunden, während manuelles " +
          "Nachformatieren jedes Mal von vorn beginnt.",
      ],
      keyPoints: [
        "Vorlage statt direkter Formatierung – spätestens ab der zweiten Wiederholung.",
        "Ebenen nach Bedeutung wählen und lückenlos verschachteln.",
        "Titel und Zitate haben eigene Vorlagen.",
        "Verzeichnisse und Navigation entstehen aus den Überschriftenvorlagen.",
      ],
    },
  ],
};
